import styles from './styles.module.scss';
import {
  useKycInformationHandler,
  useLottiePlayer,
  useTranslation,
} from '@starsoft/common/hooks';
import UserIdentitiesFormContentSkeleton from './Skeleton';
import { UserIdentitiesFormContentProps } from './props';
import KYCUserIdentityFormContentRenderer from './Renderer';
import KYCUserIdentityFormContentSteps from './Steps';
import { ErrorCard } from '@starsoft/common/components';

/**
 * UserIdentitiesFormContent Component
 * Renders the main content for the KYC user identity form, including steps, animations,
 * and the appropriate form renderer based on the current KYC step.
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} [props.isContainerColor] - Whether to use container background color styling
 * @param {bigint|null} [props.externalOriginId] - External origin ID to override default origin
 * @returns {JSX.Element} The rendered form content, skeleton loader, or error card
 *
 * @example
 * // Basic usage
 * <UserIdentitiesFormContent
 *   isContainerColor={true}
 *   externalOriginId={123n}
 * />
 *
 * // Without container color and external origin
 * <UserIdentitiesFormContent />
 */
export default function UserIdentitiesFormContent({
  isContainerColor,
  externalOriginId,
}: UserIdentitiesFormContentProps) {
  const { t } = useTranslation('common');
  const {
    useKycMeByOriginIdQuery: { isPending, error, refetch },
    lottie,
    title,
    description,
  } = useKycInformationHandler();

  const { lottieAnimationRef } = useLottiePlayer({
    path: `/${lottie}`,
    dependecyArray: [isPending],
  });

  if (isPending) {
    return (
      <UserIdentitiesFormContentSkeleton isContainerColor={isContainerColor} />
    );
  }

  if (error) {
    return <ErrorCard error={error} refetch={refetch} />;
  }

  return (
    <div className={styles.container}>
      <KYCUserIdentityFormContentSteps />
      <div
        className={`${styles.container__column} ${styles['container__column--small']} ${styles['container__column--center']}`}
      >
        <div className={styles.container__lottie} ref={lottieAnimationRef} />
        <div className={styles.container__text}>
          <h2 className={styles.container__title}>{t(title)}</h2>
          <p className={styles.container__description}>{t(description)}</p>
        </div>
      </div>
      <KYCUserIdentityFormContentRenderer
        isContainerColor={isContainerColor}
        externalOriginId={externalOriginId}
      />
    </div>
  );
}
